import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { IOPayload } from '@connect-services/client/models/notification.model';
import { SESSION_STORE_KEY } from '@connect-services/client/resolvers/session.resolver';
import {
    FEATURE_SESSION_STORE_KEY,
    FEATURE_TRACKED_STORE_KEY,
} from '@connect-services/client/services/feature-session.service';
import { NotificationService } from '@connect-services/client/services/notification.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { SessionService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule, RouterOutlet],
    selector: 'app-root',
    template: `
        @if (!isLoading()) {
            <router-outlet />
        }
    `,
})
export class AppComponent implements OnInit, OnDestroy {
    public readonly isLoading: WritableSignal<boolean> = signal(true);

    private readonly notificationService = inject(NotificationService<IOPayload>);
    private readonly securityService = inject(SecurityService);
    private readonly sessionService = inject(SessionService);
    private readonly translateService = inject(TranslateService);

    public ngOnDestroy(): void {
        window.removeEventListener('beforeunload', () => this.endSession());
    }

    public async ngOnInit(): Promise<void> {
        // Here, initLang() is awaited and can take some time to load, though, we need initSession() to be trigger as soon as possible because of the guard, so make sure it is always initiated first.
        await this.securityService.initSession();
        this.notificationService.connect();
        await this.translateService.initLang();

        window.addEventListener('beforeunload', () => this.endSession());
        this.isLoading.set(false);
    }

    private async endSession(): Promise<void> {
        const sessionId = sessionStorage.getItem(SESSION_STORE_KEY);

        if (!sessionId) {
            return;
        }

        sessionStorage.removeItem(FEATURE_SESSION_STORE_KEY);
        sessionStorage.removeItem(FEATURE_TRACKED_STORE_KEY);
        sessionStorage.removeItem('session');

        await firstValueFrom(
            this.sessionService.sessionControllerPatch({
                id: sessionId,
            }),
        );
    }
}
